import {useInfiniteQuery, UseInfiniteQueryOptions} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {httpGetConversationMessages} from '../http/conversations.http'
import {ConversationMessagesList} from '@/features/conversations/types.ts'
import {QUERY_KEYS} from '@/queryClient.ts'
import {parseAxiosPromise} from '@/axiosInstance.ts'
import {captureException} from '@sentry/react'
import {infiniteQueryFetchNextPage} from '@/utilities/helpers'

/**
 * Hook to fetch conversation messages with pagination
 * @param conversationId ID of the conversation to get messages for
 * @param perPage Number of results per page (defaults to 30)
 * @param options Additional query options
 */
export const useConversationMessages = (
    conversationId: string,
    perPage: number = 30,
    options?: Pick<UseInfiniteQueryOptions<ConversationMessagesList, AxiosError>, 'enabled'>
) => {
    const query = useInfiniteQuery({
        queryKey: [QUERY_KEYS.CONVERSATION_MESSAGES, conversationId, perPage],
        queryFn: ({pageParam = 1}) =>
            parseAxiosPromise({
                axiosPromise: httpGetConversationMessages(conversationId, {
                    params: {
                        page: pageParam,
                        limit: perPage
                    }
                }),
                onZodError: captureException,
                responseShape: ConversationMessagesList
            }),
        initialPageParam: 1,
        enabled: !!conversationId,
        ...options,
        staleTime: 0,
        gcTime: 0,
        getNextPageParam: lastPage => {
            const meta = lastPage.meta
            return meta.currentPage < meta.lastPage ? meta.currentPage + 1 : null
        }
    })

    return {
        ...query,
        fetchNextPage: () => infiniteQueryFetchNextPage(query),
        messages: query.data?.pages.flatMap(page => page.data) || []
    }
}
